import { useEffect, useState } from 'react'
import {
  Alert, Box, Chip, Snackbar, Tab, Tabs, Typography,
} from '@mui/material'
import TuneIcon from '@mui/icons-material/Tune'
import FunctionsIcon from '@mui/icons-material/Functions'
import AccountTreeIcon from '@mui/icons-material/AccountTree'
import HistoryIcon from '@mui/icons-material/History'
import BarChartIcon from '@mui/icons-material/BarChart'
import { useDispatch } from 'react-redux'
import { deleteAdminConfig } from '../../reducers/Beacon4PerformanceNetworkOverviewAdminReducer'
import Beacon4PerformanceNetworkOverviewAdminOverviewPanel from './Beacon4PerformanceNetworkOverviewAdminOverviewPanel'
import Beacon4PerformanceNetworkOverviewAdminFormulaPanel from './Beacon4PerformanceNetworkOverviewAdminFormulaPanel'
import Beacon4PerformanceNetworkOverviewAdminBreakdownsPanel from './Beacon4PerformanceNetworkOverviewAdminBreakdownsPanel'
import Beacon4PerformanceNetworkOverviewAdminAuditPanel from './Beacon4PerformanceNetworkOverviewAdminAuditPanel'
import Beacon4PerformanceNetworkOverviewAdminConfirmDialog from './Beacon4PerformanceNetworkOverviewAdminConfirmDialog'

const TABS = [
  { label: 'Overview',   icon: <TuneIcon sx={{ fontSize: 16 }} /> },
  { label: 'Formula',    icon: <FunctionsIcon sx={{ fontSize: 16 }} /> },
  { label: 'Breakdowns', icon: <AccountTreeIcon sx={{ fontSize: 16 }} /> },
  { label: 'Audit',      icon: <HistoryIcon sx={{ fontSize: 16 }} /> },
]

/**
 * Detail pane — everything about one KPI, scoped by the selection in the list.
 * Four sub-tabs keep business metadata, the SQL formula, the chart breakdowns
 * and the audit history separate but all in the same context.
 */
export default function Beacon4PerformanceNetworkOverviewAdminKpiDetail({
  config, onEditMetadata, onClone, openOnFormulaFor,
}) {
  const dispatch = useDispatch()
  const [tab, setTab]       = useState(0)
  const [confirmDel, setConfirmDel] = useState(false)
  const [delBusy, setDelBusy]       = useState(false)
  const [snack, setSnack]           = useState(null) // { msg, sev }

  const notify = (msg, sev = 'success') => setSnack({ msg, sev })

  // Reset whenever the selected KPI changes — to Overview normally, or straight
  // to Formula when the parent asks for it by id. That request only ever names
  // a freshly created KPI, which has no data source yet, so Overview would show
  // an empty shell with no indication of the next step.
  const FORMULA_TAB = 1
  useEffect(() => {
    setTab(openOnFormulaFor != null && openOnFormulaFor === config?.id ? FORMULA_TAB : 0)
  }, [config?.id, openOnFormulaFor])

  if (!config) {
    return (
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
                 justifyContent: 'center', color: 'text.disabled', gap: 1.5 }}>
        <BarChartIcon sx={{ fontSize: 56, opacity: 0.25 }} />
        <Typography sx={{ fontSize: 13, fontWeight: 500 }}>Select a KPI</Typography>
        <Typography sx={{ fontSize: 11 }}>Pick a KPI from the list, or create a new one.</Typography>
      </Box>
    )
  }

  const onConfirmDelete = async (note) => {
    setDelBusy(true)
    const res = await dispatch(deleteAdminConfig({ id: config.id, note: note || 'Soft delete via admin UI' }))
    setDelBusy(false)
    setConfirmDel(false)
    notify(res?.error ? 'Could not delete KPI.' : 'KPI marked inactive.', res?.error ? 'error' : 'success')
  }

  return (
    <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ px: 3, pt: 2, pb: 0, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography sx={{ fontSize: 9.5, fontWeight: 700, color: 'text.disabled',
                            textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            Selected KPI
          </Typography>
          <Chip label={config.tech} size="small" sx={{ height: 18, fontSize: 10 }} />
          <Typography sx={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 700 }}>{config.kpiKey}</Typography>
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>· {config.kpiName}</Typography>
          {!config.isActive && <Chip label="Inactive" size="small" sx={{ height: 18, fontSize: 10 }} />}
        </Box>
        <Tabs
          value={tab} onChange={(_, v) => setTab(v)}
          sx={{ minHeight: 38, '& .MuiTab-root': { fontSize: 12, minHeight: 38, textTransform: 'none' } }}
        >
          {TABS.map(t => <Tab key={t.label} icon={t.icon} iconPosition="start" label={t.label} />)}
        </Tabs>
      </Box>

      {/* Panel body */}
      <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
        {tab === 0 && (
          <Beacon4PerformanceNetworkOverviewAdminOverviewPanel
            config={config}
            onEdit={() => onEditMetadata(config)}
            onClone={() => onClone(config)}
            onDelete={() => setConfirmDel(true)}
          />
        )}
        {tab === 1 && <Beacon4PerformanceNetworkOverviewAdminFormulaPanel config={config} notify={notify} />}
        {tab === 2 && <Beacon4PerformanceNetworkOverviewAdminBreakdownsPanel config={config} notify={notify} />}
        {tab === 3 && <Beacon4PerformanceNetworkOverviewAdminAuditPanel config={config} notify={notify} />}
      </Box>

      <Beacon4PerformanceNetworkOverviewAdminConfirmDialog
        open={confirmDel}
        title="Soft-delete this KPI?"
        message={`${config.kpiName} (${config.kpiKey}) will be marked inactive. It is hidden from the dashboard but its mappings and audit history are preserved.`}
        confirmLabel="Soft delete" confirmColor="error"
        withNote busy={delBusy}
        onConfirm={onConfirmDelete}
        onClose={() => setConfirmDel(false)}
      />

      <Snackbar
        open={!!snack} autoHideDuration={3000} onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snack && (
          <Alert severity={snack.sev} variant="filled" onClose={() => setSnack(null)} sx={{ fontSize: 12 }}>
            {snack.msg}
          </Alert>
        )}
      </Snackbar>
    </Box>
  )
}